import type { TsTypeType } from '../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { Actionable } from './actionable.js'
import { ExecutionContext } from '../script/execution-context.js'
import { BoVisitor } from './bo-visitor.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

export const EntityMethodAvailabilities = [
	{ id: 'serverOnly', name: 'On server only', executionLocation: 'server' },
	{ id: 'clientOnly', name: 'On client only', executionLocation: 'client' },
	{
		id: 'serverAndClient',
		name: 'On server and client',
		executionLocation: 'serverAndClient',
	},
	{
		id: 'calculation',
		name: 'Calculated field (read-only, available on server and client)',
		executionLocation: 'calculation',
	},
	{
		id: 'callableFromClient',
		name: 'Runs on server, but is callable from client',
		executionLocation: 'server',
	},
] as const
export type EntityMethodAvailability = (typeof EntityMethodAvailabilities)[number]['id']
export const EntityMethodAccessModes = {
	accessControl: 'Available based on access control',
	loggedIn: 'Available for logged-in users',
	public: 'Available publicly (not restricted)',
}
export type EntityMethodAccessMode = keyof typeof EntityMethodAccessModes

@jsonClass('EntityMethod')
export class EntityMethod extends Actionable {
	@jsonProperty(Boolean) isStatic: boolean = false
	@jsonProperty(Boolean) isAsync: boolean = false
	@jsonProperty(Boolean) isOfInterest: boolean = true
	@jsonProperty(String)
	returnType: TsTypeType = 'void'
	@jsonProperty(String)
	availability: EntityMethodAvailability = 'serverOnly'
	@jsonProperty(String)
	accessMode: EntityMethodAccessMode = 'accessControl'
	@jsonProperty(Boolean) isSearchableByDefault: boolean = false

	constructor(init?: Partial<EntityMethod>) {
		super()
		assignCommonProperties(this, init)
	}

	getSignature(
		executionContext: ExecutionContext,
		forDeclaration: boolean,
		thisType?: string,
		includeFunctionKeyword = false,
	) {
		const isCallableAndNeedsAsync =
			this.availability == 'callableFromClient' &&
			executionContext.needsAsyncForClientCallableMethods()
		const promisify = this.isAsync || isCallableAndNeedsAsync

		const returnType = promisify
			? `Promise<${this.returnType}>`
			: this.returnType
		const returns = returnType ? `: ${returnType}` : ''

		const inputDecls: string[] = []
		if (thisType) {
			if (this.isStatic) {
				thisType = `typeof ${thisType}`
			}
			inputDecls.push(`this: ${thisType}`)
			if (this.availability == 'callableFromClient' && !this.isStatic) {
				inputDecls.push(`clientThis: ${thisType}`)
			}
		}
		inputDecls.push(
			...this.inputs.map((i) => {
				if (forDeclaration) {
					return `${i.name}${i.defaultExpression ? '?' : ''}: ${i.type}`
				} else {
					return (
						`${i.name}: ${i.type}` +
						(i.defaultExpression ? ` = ${i.defaultExpression}` : '')
					)
				}
			}),
		)

		const keywords: string[] = []
		if (this.isStatic && forDeclaration) keywords.push('static')
		if (!forDeclaration && (this.isAsync || promisify)) keywords.push('async')
		if (includeFunctionKeyword) keywords.push('function')

		return `${keywords.join(' ')} ${this.name}(${inputDecls.join(', ')})${returns}`
	}

	getExecutionLocation() {
		return EntityMethodAvailabilities.find((av) => av.id == this.availability)
			?.executionLocation
	}

	visit(visitor: BoVisitor, pathPrefix: (string | number)[]): void {
		// super.visit(visitor, pathPrefix)
		visitor.visitTsCode(
			this.code,
			this,
			'method',
			[...pathPrefix, 'code'],
			(newCode) => (this.code = newCode),
		)
		visitor.visitTsCode(
			this.returnType,
			this,
			'type',
			[...pathPrefix, 'returnType'],
			(newType) => (this.returnType = newType),
		)
		this.inputs.forEach((input, idx) =>
			input.visit(visitor, [...pathPrefix, 'inputs', idx]),
		)
	}
}
